/*
 * ui.js
 * Header navigation, disclosures, modals, sliders and section-level UI components.
 * Depends on core.js (trapFocus, removeTrapFocus, onKeyUpEscape, pauseAllMedia,
 * debounce, isRtl, ...), which must be loaded first.
 *
 * Classes that other scripts extend (DetailsDisclosure, DetailsModal, ModalDialog,
 * DeferredMedia, MenuDrawer, SliderComponent) are declared at top level on purpose.
 */

/* ---------- Shared helpers ---------- */

const uiHelpers = {
  reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)'),

  define(name, constructor) {
    if (!customElements.get(name)) customElements.define(name, constructor);
  },

  toNumber(value, fallback) {
    const number = Number.parseFloat(value);
    return Number.isNaN(number) ? fallback : number;
  },

  isEscape(event) {
    return event.code?.toUpperCase() === 'ESCAPE' || event.key === 'Escape';
  },

  // Pauses (or mutes) every playable media inside `container`.
  pauseMediaIn(container) {
    if (!container) return;

    container.querySelectorAll('.js-youtube').forEach((iframe) => {
      iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
    });
    container.querySelectorAll('.js-vimeo').forEach((iframe) => {
      iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
    });
    container.querySelectorAll('internal-video').forEach((element) => {
      if (element.dataset.actionOnInactive === 'mute' && element.video) {
        element.video.muted = true;
        element.classList.add('internal-video--muted');
      } else if (typeof element.pause === 'function') {
        element.pause();
      }
    });
    container.querySelectorAll('video').forEach((video) => {
      if (!video.closest('internal-video')) video.pause();
    });
    container.querySelectorAll('product-model').forEach((model) => model.modelViewerUI?.pause());
  },
};

/* ---------- Accessible <details> summaries (Dawn convention) ---------- */

const enhancedSummaries = new WeakSet();

function initDisclosureSummaries(root = document) {
  root.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
    if (enhancedSummaries.has(summary)) return;
    enhancedSummaries.add(summary);

    const details = summary.parentElement;
    summary.setAttribute('role', 'button');
    summary.setAttribute('aria-expanded', details.hasAttribute('open'));

    const content = summary.nextElementSibling;
    if (content?.id) summary.setAttribute('aria-controls', content.id);

    summary.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !details.hasAttribute('open'));
    });

    // Drawers handle Escape themselves.
    if (summary.closest('header-drawer, menu-drawer')) return;
    details.addEventListener('keyup', onKeyUpEscape);
  });
}

initDisclosureSummaries();
document.addEventListener('shopify:section:load', (event) => initDisclosureSummaries(event.target));

/* ---------- details-disclosure / header-menu / products-mega-menu ---------- */

class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    if (!this.mainDetailsToggle) return;

    this.summary = this.mainDetailsToggle.querySelector('summary');
    this.content = this.summary?.nextElementSibling || null;
    this.onDocumentClick = this.onDocumentClick.bind(this);

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
    this.mainDetailsToggle.addEventListener('keyup', (event) => {
      if (!uiHelpers.isEscape(event) || !this.isOpen) return;
      this.close();
      this.summary?.focus();
    });
  }

  get isOpen() {
    return Boolean(this.mainDetailsToggle?.hasAttribute('open'));
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  // Safari does not move focus on click, so also close on outside clicks.
  onDocumentClick(event) {
    if (!this.contains(event.target)) this.close();
  }

  onToggle() {
    if (!this.mainDetailsToggle) return;
    const open = this.isOpen;
    this.summary?.setAttribute('aria-expanded', open);

    if (open) {
      document.addEventListener('click', this.onDocumentClick);
    } else {
      document.removeEventListener('click', this.onDocumentClick);
    }

    if (!this.content || typeof this.content.getAnimations !== 'function') return;
    if (!this.animations) this.animations = this.content.getAnimations();
    this.animations.forEach((animation) => (open ? animation.play() : animation.cancel()));
  }

  close() {
    if (!this.mainDetailsToggle) return;
    this.mainDetailsToggle.removeAttribute('open');
    this.summary?.setAttribute('aria-expanded', false);
  }
}

uiHelpers.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = this.closest('.header-wrapper') || document.querySelector('.header-wrapper');
  }

  onToggle() {
    super.onToggle();
    if (!this.header) return;

    // Read by the sticky-header element (sections/header.liquid).
    this.header.preventHide = this.isOpen;

    const root = document.documentElement;
    if (root.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    root.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

uiHelpers.define('header-menu', HeaderMenu);

class ProductsMegaMenu extends HeaderMenu {
  constructor() {
    super();
    if (!this.mainDetailsToggle) return;

    this.items = Array.from(this.querySelectorAll('.products-mega-menu__item'));
    this.items.forEach((item) => {
      item.addEventListener('mouseenter', () => this.setActiveItem(item));
      item.addEventListener('focusin', () => this.setActiveItem(item));
    });

    this.querySelector('.products-mega-menu__overlay')?.addEventListener('click', () => this.close());
  }

  setActiveItem(activeItem) {
    this.items.forEach((item) => {
      item.classList.toggle('products-mega-menu__item--active', item === activeItem);
    });
  }
}

uiHelpers.define('products-mega-menu', ProductsMegaMenu);

/* ---------- details-modal (header search) ---------- */

class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    if (!this.detailsContainer || !this.summaryToggle) return;

    this.onBodyClickEvent = this.onBodyClick.bind(this);

    this.detailsContainer.addEventListener('keyup', (event) => {
      if (uiHelpers.isEscape(event)) this.close();
    });
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    this.summaryToggle.setAttribute('role', 'button');
    this.summaryToggle.setAttribute('aria-expanded', 'false');

    const closeButton =
      this.querySelector('.modal__close-button') || this.querySelector('button[type="button"]');
    closeButton?.addEventListener('click', () => this.close());
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  onSummaryClick(event) {
    event.preventDefault();
    this.isOpen() ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }

  open() {
    this.detailsContainer.setAttribute('open', '');
    this.summaryToggle.setAttribute('aria-expanded', 'true');
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('overflow-hidden');

    const container = this.detailsContainer.querySelector('[tabindex="-1"]') || this.detailsContainer;
    trapFocus(container, this.detailsContainer.querySelector('input:not([type="hidden"])'));
  }

  close(focusToggle = true) {
    if (!this.detailsContainer) return;
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute('open');
    this.summaryToggle.setAttribute('aria-expanded', 'false');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('overflow-hidden');
  }
}

uiHelpers.define('details-modal', DetailsModal);

/* ---------- menu-drawer / header-drawer ---------- */

class MenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    if (!this.mainDetailsToggle) return;

    this.onSummaryClick = this.onSummaryClick.bind(this);
    this.onCloseButtonClick = this.onCloseButtonClick.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.bindEvents();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  // Also called by facets.js after the mobile filter form is re-rendered.
  // Handlers are stable references, so repeated calls never double-bind.
  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) => {
      summary.addEventListener('click', this.onSummaryClick);
    });
    this.querySelectorAll(
      '.menu-drawer__close-button, .mobile-facets__close-button, .menu-drawer__close-menu-btn'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick));
  }

  get mainSummary() {
    return this.mainDetailsToggle.querySelector('summary');
  }

  onKeyUp(event) {
    if (!uiHelpers.isEscape(event)) return;
    const openDetails = event.target.closest('details[open]');
    if (!openDetails) return;

    if (openDetails === this.mainDetailsToggle) {
      this.closeMenuDrawer(event, this.mainSummary);
    } else {
      this.closeSubmenu(openDetails);
    }
  }

  onSummaryClick(event) {
    const summary = event.currentTarget;
    const details = summary.parentNode;
    const isOpen = details.hasAttribute('open');

    if (details === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summary) : this.openMenuDrawer(summary);
      document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      return;
    }

    // Opening a nested submenu: let <details> open first, then slide it in.
    const submenu = summary.nextElementSibling;
    const parentMenu = details.closest('.has-submenu');
    setTimeout(() => {
      details.classList.add('menu-opening');
      summary.setAttribute('aria-expanded', true);
      parentMenu?.classList.add('submenu-open');
      if (!submenu) return;

      const focusSubmenu = () => trapFocus(submenu, details.querySelector('button'));
      if (uiHelpers.reducedMotion.matches) {
        focusSubmenu();
      } else {
        submenu.addEventListener('transitionend', focusSubmenu, { once: true });
      }
    }, 100);
  }

  openMenuDrawer(summary) {
    setTimeout(() => this.mainDetailsToggle.classList.add('menu-opening'));
    summary.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summary);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    setTimeout(() => document.addEventListener('click', this.onDocumentClick));
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((menu) => {
      menu.classList.remove('submenu-open');
    });
    this.mainSummary?.setAttribute('aria-expanded', false);
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    document.removeEventListener('click', this.onDocumentClick);
    removeTrapFocus(elementToFocus || null);
    this.closeAnimation(this.mainDetailsToggle);
  }

  onDocumentClick(event) {
    if (!this.mainDetailsToggle.hasAttribute('open') || this.contains(event.target)) return;
    this.closeMenuDrawer(event, null);
  }

  onCloseButtonClick(event) {
    const button = event.currentTarget;
    const details = button.closest('details');
    if (!details) return;

    if (details === this.mainDetailsToggle || button.classList.contains('menu-drawer__close-menu-btn')) {
      this.closeMenuDrawer(event, this.mainSummary);
    } else {
      this.closeSubmenu(details);
    }
  }

  closeSubmenu(details) {
    details.closest('.submenu-open')?.classList.remove('submenu-open');
    details.classList.remove('menu-opening');
    const summary = details.querySelector('summary');
    summary?.setAttribute('aria-expanded', false);
    removeTrapFocus(summary);
    this.closeAnimation(details);
  }

  // Remove [open] only after the slide-out transition (~400ms) has played.
  closeAnimation(details) {
    let start;
    const step = (time) => {
      if (start === undefined) start = time;
      if (time - start < 400) {
        window.requestAnimationFrame(step);
        return;
      }
      details.removeAttribute('open');
      const parentDetails = details.parentElement?.closest('details[open]');
      if (parentDetails) trapFocus(parentDetails, details.querySelector('summary'));
    };
    window.requestAnimationFrame(step);
  }
}

uiHelpers.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    if (!this.mainDetailsToggle) return;

    this.onResize = this.onResize.bind(this);

    // A tablet-only drawer must close when the viewport grows to desktop size.
    if (this.dataset.breakpoint === 'tablet') {
      window.matchMedia('(min-width: 990px)').addEventListener('change', (event) => {
        if (event.matches && this.mainDetailsToggle.hasAttribute('open')) {
          this.closeMenuDrawer(event, this.mainSummary);
        }
      });
    }
  }

  setHeaderBottomPosition() {
    this.header = this.header || document.querySelector('.section-header');
    if (!this.header) return;

    const wrapper = this.closest('.header-wrapper');
    const borderOffset = wrapper?.classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${Math.floor(this.header.getBoundingClientRect().bottom - borderOffset)}px`
    );
  }

  openMenuDrawer(summary) {
    this.setHeaderBottomPosition();
    this.header?.classList.add('menu-open');
    window.addEventListener('resize', this.onResize);
    super.openMenuDrawer(summary);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (event === undefined) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header?.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize() {
    this.setHeaderBottomPosition();
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  }
}

uiHelpers.define('header-drawer', HeaderDrawer);

/* ---------- modal-dialog / modal-opener ---------- */

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]')?.addEventListener('click', () => this.hide(false));

    this.addEventListener('keyup', (event) => {
      if (uiHelpers.isEscape(event)) this.hide();
    });

    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  // Modals live at the end of <body> so ancestors' transforms/overflow cannot clip them.
  connectedCallback() {
    if (this.moved) return;
    this.moved = true;

    if (this.id) {
      document.querySelectorAll(`body > [id="${CSS.escape(this.id)}"]`).forEach((stale) => {
        if (stale !== this) stale.remove();
      });
    }
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');

    const popup = this.querySelector('.template-popup');
    if (popup && typeof popup.loadContent === 'function') popup.loadContent();

    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}

uiHelpers.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();
    // Prefer the dedicated dialog trigger; product media openers also contain video buttons.
    const button = this.querySelector('button[aria-haspopup="dialog"]') || this.querySelector('button');
    if (!button) return;

    button.addEventListener('click', (event) => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (!modal || typeof modal.show !== 'function') return;
      event.preventDefault();
      modal.show(button);
    });
  }
}

uiHelpers.define('modal-opener', ModalOpener);

/* ---------- deferred-media ---------- */

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    poster?.addEventListener('click', () => this.loadContent());
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (this.getAttribute('loaded')) return;

    const template = this.querySelector('template');
    if (!template) return;

    const source =
      template.content.querySelector('video, model-viewer, iframe') || template.content.firstElementChild;
    if (!source) return;

    this.setAttribute('loaded', 'true');
    const media = this.appendChild(source.cloneNode(true));

    if (focus) media.focus();
    if (media.nodeName === 'VIDEO' && media.hasAttribute('autoplay')) {
      media.play().catch(() => {});
    }
  }
}

uiHelpers.define('deferred-media', DeferredMedia);

/* ---------- product-recommendations ---------- */

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.observer || this.loaded || !this.dataset.url) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        this.observer.disconnect();
        this.observer = null;
        this.load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.observer = null;
  }

  async load() {
    try {
      const response = await fetch(this.dataset.url);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const recommendations = html.querySelector('product-recommendations');
      this.loaded = true;

      if (recommendations && recommendations.innerHTML.trim().length) {
        this.innerHTML = recommendations.innerHTML;
      }

      if (this.classList.contains('complementary-products') && !this.querySelector('slideshow-component')) {
        this.remove();
        return;
      }

      if (html.querySelector('.grid__item')) this.classList.add('product-recommendations--loaded');
    } catch (error) {
      console.error(error);
    }
  }
}

uiHelpers.define('product-recommendations', ProductRecommendations);

/* ---------- copy-button ---------- */

class CopyButton extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    if (!this.hasAttribute('role')) this.setAttribute('role', 'button');
    if (!this.hasAttribute('tabindex')) this.tabIndex = 0;

    this.addEventListener('click', () => this.copy());
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      this.copy();
    });
  }

  async copy() {
    const text = this.dataset.content || '';

    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback for insecure contexts / older browsers.
      const field = document.createElement('textarea');
      field.value = text;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      document.body.appendChild(field);
      field.select();
      document.execCommand('copy');
      field.remove();
    }

    this.dataset.success = 'true';
    clearTimeout(this.resetTimer);
    this.resetTimer = setTimeout(() => {
      this.dataset.success = 'false';
    }, 2000);
  }
}

uiHelpers.define('copy-button', CopyButton);

/* ---------- slider-component (native scroll-snap slider) ---------- */

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.sliderItemsToShow = [];
    this.currentPage = 1;
    this.totalPages = 1;

    if (!this.slider) return;

    this.initPages();
    this.resizeObserver = new ResizeObserver(() => this.initPages());
    this.resizeObserver.observe(this.slider);
    this.slider.addEventListener('scroll', () => this.update(), { passive: true });

    this.prevButton?.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton?.addEventListener('click', this.onButtonClick.bind(this));
    this.dotButtons.forEach((dot) => dot.addEventListener('click', this.onDotClick.bind(this)));
  }

  get dotButtons() {
    return Array.from(this.querySelectorAll('button.slider-counter__link'));
  }

  // Scroll distance from the slider's start edge, positive in both LTR and RTL.
  get scrollPosition() {
    return Math.abs(this.slider.scrollLeft);
  }

  scrollToPosition(position) {
    const max = Math.max(this.slider.scrollWidth - this.slider.clientWidth, 0);
    const clamped = Math.min(Math.max(position, 0), max);
    this.slider.scrollTo({ left: isRtl ? -clamped : clamped });
  }

  // Position of an item along the scroll axis, measured from the scroll origin.
  getItemStart(item) {
    const sliderRect = this.slider.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const offset = isRtl ? sliderRect.right - itemRect.right : itemRect.left - sliderRect.left;
    return offset + this.scrollPosition;
  }

  initPages() {
    if (!this.slider) return;
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((item) => item.clientWidth > 0);

    if (this.sliderItemsToShow.length < 2) {
      this.sliderItemOffset = 0;
      this.slidesPerPage = 1;
      this.totalPages = this.sliderItemsToShow.length || 1;
      this.update();
      return;
    }

    const [first, second] = this.sliderItemsToShow;
    const firstStart = this.getItemStart(first);
    this.sliderItemOffset = Math.abs(this.getItemStart(second) - firstStart) || first.clientWidth;
    this.slidesPerPage = Math.max(
      1,
      Math.floor((this.slider.clientWidth - firstStart) / this.sliderItemOffset)
    );
    this.totalPages = Math.max(1, this.sliderItemsToShow.length - this.slidesPerPage + 1);
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    if (!this.slider || !this.sliderItemsToShow) return;

    const previousPage = this.currentPage;
    const page = this.sliderItemOffset ? Math.round(this.scrollPosition / this.sliderItemOffset) + 1 : 1;
    this.currentPage = Math.min(Math.max(page, 1), Math.max(this.sliderItemsToShow.length, 1));

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    this.updateDots();

    if (this.currentPage !== previousPage) {
      const currentElement = this.sliderItemsToShow[this.currentPage - 1];
      this.onPageChange(currentElement);
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: { currentPage: this.currentPage, currentElement },
        })
      );
    }

    this.updateButtons();
  }

  updateButtons() {
    if (this.enableSliderLooping || !this.prevButton || !this.nextButton) return;

    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
    const atStart = this.scrollPosition <= 1;
    const atEnd = this.scrollPosition >= maxScroll - 1;

    this.prevButton.toggleAttribute('disabled', atStart);
    this.nextButton.toggleAttribute('disabled', atEnd);
  }

  // Dots are grouped per container; hidden dots (filtered variant media) are skipped.
  visibleDotsIn(container) {
    return Array.from(container.querySelectorAll('button.slider-counter__link')).filter(
      (dot) => !dot.classList.contains('hidden')
    );
  }

  updateDots() {
    const containers = new Set(this.dotButtons.map((dot) => dot.parentElement));
    containers.forEach((container) => {
      this.visibleDotsIn(container).forEach((dot, index) => {
        const active = index === this.currentPage - 1;
        dot.classList.toggle('slider-counter__link--active', active);
        if (active) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    });
  }

  onDotClick(event) {
    event.preventDefault();
    const dot = event.currentTarget;
    const index = this.visibleDotsIn(dot.parentElement).indexOf(dot);
    if (index >= 0) this.goToPage(index + 1);
  }

  goToPage(page) {
    if (!this.slider) return;
    this.scrollToPosition((page - 1) * (this.sliderItemOffset || 0));
  }

  onPageChange(currentElement) {
    if (!currentElement) return;

    if (this.dataset.updateActive === 'true') {
      this.sliderItems.forEach((item) => item.classList.toggle('is-active', item === currentElement));
    }

    if (this.dataset.pauseVideos === 'true') {
      this.sliderItems.forEach((item) => {
        if (item !== currentElement) uiHelpers.pauseMediaIn(item);
      });
    }
  }

  // `vertical` is accepted for API compatibility; a numeric second argument is treated as the offset.
  isSlideVisible(element, vertical = false, offset = 0) {
    if (!element || !this.slider) return false;
    if (typeof vertical === 'number') offset = vertical;

    const start = this.getItemStart(element);
    const end = start + element.clientWidth;
    const visibleStart = this.scrollPosition;
    const visibleEnd = visibleStart + this.slider.clientWidth - offset;
    return start >= visibleStart - 1 && end <= visibleEnd + 1;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = Number.parseInt(event.currentTarget.dataset.step, 10) || 1;
    const direction = event.currentTarget.name === 'next' ? 1 : -1;
    this.slideScrollPosition = this.scrollPosition + direction * step * (this.sliderItemOffset || 0);
    this.scrollToPosition(this.slideScrollPosition);
  }
}

uiHelpers.define('slider-component', SliderComponent);

/* ---------- slideshow-component ---------- */

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.enableSliderLooping = true;
    if (!this.slider) return;

    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.prevButton?.removeAttribute('disabled');
    this.nextButton?.removeAttribute('disabled');

    this.slider.addEventListener('scroll', debounce(() => this.setSlideVisibility(), 50), { passive: true });
    this.setSlideVisibility();

    if (this.slider.dataset.autoplay === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = uiHelpers.toNumber(this.slider.dataset.speed, 5) * 1000;

    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
    if (this.sliderAutoplayButton) {
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else if (uiHelpers.reducedMotion.matches) {
      this.pause();
    } else {
      this.play();
    }
  }

  onButtonClick(event) {
    const goingNext = event.currentTarget.name === 'next';
    const lastPage = this.sliderItemsToShow.length;

    if (goingNext && this.currentPage >= lastPage) {
      event.preventDefault();
      this.goToPage(1);
    } else if (!goingNext && this.currentPage <= 1) {
      event.preventDefault();
      this.goToPage(lastPage);
    } else {
      super.onButtonClick(event);
    }
  }

  autoRotateSlides() {
    const lastPage = this.sliderItemsToShow.length;
    this.goToPage(this.currentPage >= lastPage ? 1 : this.currentPage + 1);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  isAutoplayButton(target) {
    return Boolean(this.sliderAutoplayButton && this.sliderAutoplayButton.contains(target));
  }

  focusInHandling(event) {
    if (!this.sliderAutoplayButton) {
      this.pause();
      return;
    }
    if (this.isAutoplayButton(event.target) && this.autoplayButtonIsSetToPlay) {
      this.play();
    } else if (this.autoplayButtonIsSetToPlay) {
      this.pause();
    }
  }

  focusOutHandling(event) {
    if (!this.sliderAutoplayButton) {
      if (!uiHelpers.reducedMotion.matches) this.play();
      return;
    }
    if (!this.autoplayButtonIsSetToPlay || this.isAutoplayButton(event.target)) return;
    this.play();
  }

  play() {
    if (!this.slider) return;
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(() => this.autoRotateSlides(), this.autoplaySpeed || 5000);
  }

  pause() {
    if (!this.slider) return;
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  disconnectedCallback() {
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (!this.sliderAutoplayButton) return;
    const strings = window.accessibilityStrings || {};
    this.sliderAutoplayButton.classList.toggle('slideshow__autoplay--paused', pauseAutoplay);
    const label = pauseAutoplay ? strings.playSlideshow : strings.pauseSlideshow;
    if (label) this.sliderAutoplayButton.setAttribute('aria-label', label);
  }

  // Only the visible slide is exposed to assistive tech and keyboard focus.
  setSlideVisibility() {
    this.sliderItemsToShow.forEach((item, index) => {
      const active = index === this.currentPage - 1;
      item.setAttribute('aria-hidden', active ? 'false' : 'true');
      item.querySelectorAll('a, button').forEach((link) => {
        if (active) {
          link.removeAttribute('tabindex');
        } else {
          link.setAttribute('tabindex', '-1');
        }
      });
      if (active) {
        item.removeAttribute('tabindex');
      } else {
        item.setAttribute('tabindex', '-1');
      }
    });
  }

  updateButtons() {
    // Looping slideshow: arrows are never disabled.
  }
}

uiHelpers.define('slideshow-component', SlideshowComponent);

/* ---------- splide-component (requires a global Splide library) ---------- */

class SplideComponent extends HTMLElement {
  constructor() {
    super();
    this.onResize = debounce(() => this.updateActiveSlide(), 200);
    this.onBlockSelect = this.onBlockSelect.bind(this);
  }

  connectedCallback() {
    this.mount();
    document.addEventListener('shopify:block:select', this.onBlockSelect);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.splide?.destroy(true);
    this.splide = null;
  }

  int(name, fallback) {
    const value = Number.parseInt(this.dataset[name], 10);
    return Number.isNaN(value) ? fallback : value;
  }

  buildOptions(root) {
    const data = this.dataset;
    const type = data.type || 'slide';
    const mobileType = data.typeMobile || type;
    const perPageDesktop = this.int('slidesDesktop', 3);
    const perPageMobile = this.int('slidesMobile', 1);
    const perMoveDesktop = this.int('perMoveDesktop', 1);
    const perMoveMobile = this.int('perMoveMobile', 1);
    const paddingDesktop = this.int('sidePaddingDesktop', 0);
    const paddingMobile = this.int('sidePaddingMobile', 0);
    const paddingCalcDesktop = data.paddingCalcDesktop === 'true';
    const paddingCalcMobile = data.paddingCalcMobile === 'true';
    const arrowsColor = data.arrowsColor ? ` color-${data.arrowsColor}` : '';
    const dotsColor = data.dotsColor ? ` color-${data.dotsColor} dots-custom-color` : '';

    const slideCount = root.querySelectorAll('.splide__list > .splide__slide:not(.splide__slide--clone)').length;
    const loops = type === 'loop' || mobileType === 'loop';
    const clones =
      loops && slideCount > 1
        ? Math.max(slideCount, perPageDesktop, perPageMobile, perMoveDesktop, perMoveMobile)
        : undefined;

    let drag = true;
    if (data.drag === 'false') drag = false;
    if (data.drag === 'free') drag = 'free';

    return {
      type,
      direction: isRtl ? 'rtl' : 'ltr',
      rewind: type === 'fade',
      clones,
      autoplay: data.autoplay === 'true',
      interval: uiHelpers.toNumber(data.autoplaySpeed, 5) * 1000,
      drag,
      focus: data.focus || 0,
      trimSpace: data.trimSpace !== 'false',
      arrows: data.arrows !== 'false',
      pagination: data.pagination !== 'false',
      omitEnd: data.omitEnd !== 'false',
      autoWidth: data.autoWidth === 'true',
      perPage: perPageDesktop,
      perMove: perMoveDesktop,
      gap: this.int('gapDesktop', 30),
      padding: { left: paddingCalcDesktop ? 0 : paddingDesktop, right: paddingDesktop },
      destroy: data.destroyDesktop === 'true',
      easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
      classes: {
        arrow: `splide__arrow${arrowsColor}`,
        page: `splide__pagination__page${dotsColor}`,
      },
      breakpoints: {
        749: {
          type: mobileType,
          rewind: mobileType === 'fade',
          clones,
          perPage: perPageMobile,
          perMove: perMoveMobile,
          gap: this.int('gapMobile', 15),
          padding: { left: paddingCalcMobile ? 0 : paddingMobile, right: paddingMobile },
          destroy: data.destroyMobile === 'true',
        },
      },
    };
  }

  mount() {
    this.root = this.querySelector('.splide');
    if (!this.root || typeof Splide === 'undefined') return;

    this.splide?.destroy(true);
    this.splide = new Splide(this.root, this.buildOptions(this.root));

    this.splide.on('mounted move resized', () => this.updateActiveSlide());
    if (this.dataset.pauseVideos === 'true') {
      this.splide.on('hidden', (slide) => uiHelpers.pauseMediaIn(slide.slide));
    }

    this.splide.mount();
    window.removeEventListener('resize', this.onResize);
    window.addEventListener('resize', this.onResize);
  }

  // Feeds `--active-slide-height` for the adaptive-height CSS.
  updateActiveSlide() {
    if (!this.splide) return;
    const slides = this.splide.Components?.Elements?.slides || [];
    const active = slides[this.splide.index];
    if (!active) return;

    slides.forEach((slide) => slide.classList.toggle('is-instant-active', slide === active));

    const setHeight = () => this.style.setProperty('--active-slide-height', `${active.offsetHeight}px`);
    const image = active.querySelector('img');
    if (image && !image.complete) {
      image.addEventListener('load', setHeight, { once: true });
    } else {
      setHeight();
    }
  }

  onBlockSelect(event) {
    if (!this.splide || !this.contains(event.target)) return;
    const slide = event.target.closest('.splide__slide:not(.splide__slide--clone)');
    if (!slide) return;
    const slides = Array.from(
      this.root.querySelectorAll('.splide__list > .splide__slide:not(.splide__slide--clone)')
    );
    const index = slides.indexOf(slide);
    if (index >= 0) this.splide.go(index);
  }
}

uiHelpers.define('splide-component', SplideComponent);

/* ---------- countdown-timer (cart drawer) ---------- */

const CART_TIMER_STORAGE_KEY = 'cart-countdown-end';

class CountdownTimer extends HTMLElement {
  connectedCallback() {
    // cart.js moves running timers between re-renders; keep the original deadline.
    if (!this.endTime) this.endTime = this.resolveEndTime();
    this.tick();
    clearInterval(this.interval);
    this.interval = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }

  get durationSeconds() {
    return Math.max(1, Number.parseInt(this.dataset.duration, 10) || 300);
  }

  resolveEndTime() {
    const now = Date.now();
    let stored = null;
    try {
      stored = JSON.parse(sessionStorage.getItem(CART_TIMER_STORAGE_KEY));
    } catch {
      stored = null;
    }

    if (stored && stored.duration === this.durationSeconds && stored.end > now) return stored.end;

    const end = now + this.durationSeconds * 1000;
    try {
      sessionStorage.setItem(CART_TIMER_STORAGE_KEY, JSON.stringify({ end, duration: this.durationSeconds }));
    } catch {
      // Storage unavailable (private mode); the timer simply restarts on reload.
    }
    return end;
  }

  tick() {
    const remaining = Math.max(0, Math.round((this.endTime - Date.now()) / 1000));
    this.textContent = this.format(remaining);
    this.dataset.remainingSeconds = remaining;
    if (remaining === 0) clearInterval(this.interval);
  }

  format(totalSeconds) {
    const pad = (value) => String(value).padStart(2, '0');
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return this.durationSeconds >= 3600
      ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
      : `${pad(minutes)}:${pad(seconds)}`;
  }
}

uiHelpers.define('countdown-timer', CountdownTimer);

/* ---------- tnc-chekcbox (terms checkbox gating checkout) ---------- */

const TNC_CHECKOUT_SELECTOR = '#CartDrawer-Checkout, .cart__checkout-button';
const TNC_GUARD_SELECTOR = `${TNC_CHECKOUT_SELECTOR}, .additional-checkout-buttons, .cart__dynamic-checkout-buttons`;

// The checkbox only gates checkout controls rendered in the same cart drawer / section.
function tncScopeOf(element) {
  return element.closest('cart-drawer') || element.closest('.shopify-section');
}

class TncCheckbox extends HTMLElement {
  connectedCallback() {
    if (!this.initialized) {
      this.initialized = true;
      this.setAttribute('role', 'checkbox');
      if (!this.hasAttribute('tabindex')) this.tabIndex = 0;

      this.addEventListener('click', () => this.toggle());
      this.addEventListener('keydown', (event) => {
        if (event.key !== ' ' && event.key !== 'Enter') return;
        event.preventDefault();
        this.toggle();
      });
    }
    this.applyState();
  }

  get checked() {
    return this.dataset.checked === 'true';
  }

  get scope() {
    return tncScopeOf(this) || this.parentElement || this;
  }

  toggle() {
    this.dataset.checked = String(!this.checked);
    this.applyState();
    if (this.checked) this.hideWarnings();
  }

  applyState() {
    this.setAttribute('aria-checked', String(this.checked));
    if (this.dataset.disableButton !== 'true') return;

    this.scope.querySelectorAll(TNC_CHECKOUT_SELECTOR).forEach((button) => {
      button.classList.toggle('disabled', !this.checked);
      button.setAttribute('aria-disabled', String(!this.checked));
    });
  }

  showWarning() {
    const text = this.dataset.warningText;
    if (!text) return;

    const position = this.dataset.warningPosition || 'under-checkbox';
    const warning =
      this.scope.querySelector(`.tnc-checkbox-warning--${position}`) ||
      this.scope.querySelector('.tnc-checkbox-warning--under-checkbox');
    if (!warning) return;

    warning.textContent = text;
    warning.classList.remove('hidden');
  }

  hideWarnings() {
    this.scope.querySelectorAll('.tnc-checkbox-warning').forEach((warning) => warning.classList.add('hidden'));
  }
}

uiHelpers.define('tnc-chekcbox', TncCheckbox);

// Delegated so it survives cart drawer re-renders; capture phase runs before form submission.
document.addEventListener(
  'click',
  (event) => {
    const trigger = event.target.closest?.(TNC_GUARD_SELECTOR);
    if (!trigger) return;

    const checkbox = tncScopeOf(trigger)?.querySelector('tnc-chekcbox');
    if (!checkbox || checkbox.checked) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    checkbox.showWarning();
  },
  true
);

/* ---------- hotspot-button ---------- */

class HotspotButton extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.button = this.querySelector('.hotspot-btn');
    if (!this.button) return;
    this.initialized = true;

    this.content = this.querySelector('.hotspot__content');
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.button.setAttribute('aria-expanded', 'false');

    const opensOnHover = this.dataset.openEvent === 'hover' && window.matchMedia('(hover: hover)').matches;

    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      if (opensOnHover) this.open();
      else if (this.isOpen) this.close();
      else this.open();
    });

    if (opensOnHover) {
      this.addEventListener('mouseenter', () => this.open());
      this.addEventListener('mouseleave', () => this.close());
    }

    this.querySelector('.hotspot-overlay')?.addEventListener('click', () => this.close());
    this.addEventListener('keyup', (event) => {
      if (!uiHelpers.isEscape(event) || !this.isOpen) return;
      this.close();
      this.button.focus();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  get isOpen() {
    return this.dataset.open === 'true';
  }

  open() {
    if (this.isOpen) return;

    const selector = this.dataset.section
      ? `hotspot-button[data-section="${CSS.escape(this.dataset.section)}"][data-open="true"]`
      : 'hotspot-button[data-open="true"]';
    document.querySelectorAll(selector).forEach((hotspot) => {
      if (hotspot !== this) hotspot.close();
    });

    this.updateDirection();
    this.dataset.open = 'true';
    this.button.setAttribute('aria-expanded', 'true');
    setTimeout(() => document.addEventListener('click', this.onDocumentClick));
  }

  close() {
    if (!this.isOpen) return;
    this.dataset.open = 'false';
    this.button?.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this.onDocumentClick);
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) this.close();
  }

  // Open the card below the hotspot when there is not enough room above it.
  updateDirection() {
    if (!this.content) return;
    const container = this.closest('.hotspots-image') || this.parentElement;
    const buttonRect = this.button.getBoundingClientRect();
    const containerTop = container ? container.getBoundingClientRect().top : 0;
    const spaceAbove = buttonRect.top - Math.max(containerTop, 0);
    const needed = this.content.offsetHeight + buttonRect.height / 2;
    this.dataset.direction = spaceAbove < needed ? 'bottom' : 'top';
  }
}

uiHelpers.define('hotspot-button', HotspotButton);

document.addEventListener('shopify:block:select', (event) => {
  const hotspot = event.target.closest?.('hotspot-button');
  if (hotspot && typeof hotspot.open === 'function') hotspot.open();
});
document.addEventListener('shopify:block:deselect', (event) => {
  const hotspot = event.target.closest?.('hotspot-button');
  if (hotspot && typeof hotspot.close === 'function') hotspot.close();
});

/* ---------- parallax-hero ---------- */

class ParallaxHero extends HTMLElement {
  connectedCallback() {
    this.layers = Array.from(this.querySelectorAll('.parallax-hero__layer'))
      .map((element) => ({
        element,
        scrollY: uiHelpers.toNumber(element.dataset.scrollY, 0) / 100,
        scrollX: uiHelpers.toNumber(element.dataset.scrollX, 0) / 100,
        zoom: uiHelpers.toNumber(element.dataset.zoom, 100) / 100,
        rotation: uiHelpers.toNumber(element.dataset.rotation, 0) / 100,
      }))
      // Leave untouched layers alone so their CSS transforms are preserved.
      .filter((layer) => layer.scrollY || layer.scrollX || layer.zoom !== 1 || layer.rotation);

    if (!this.layers.length || uiHelpers.reducedMotion.matches) return;

    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.render();
      });
    };

    this.observer = new IntersectionObserver((entries) => {
      this.isVisible = entries[0].isIntersecting;
      if (this.isVisible) this.onScroll();
    });
    this.observer.observe(this);

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll);
    this.render();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    if (this.onScroll) {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  render() {
    if (this.isVisible === false) return;

    const rect = this.getBoundingClientRect();
    const viewport = window.innerHeight;
    // "top": animate once the section's top edge leaves the viewport.
    // "bottom": animate as soon as the top edge enters the viewport.
    const startsAtBottom = this.dataset.animationStart === 'bottom';
    const total = startsAtBottom ? viewport + rect.height : rect.height;
    const travelled = Math.min(Math.max(startsAtBottom ? viewport - rect.top : -rect.top, 0), total);
    const progress = total > 0 ? travelled / total : 0;

    this.layers.forEach(({ element, scrollX, scrollY, zoom, rotation }) => {
      const x = travelled * scrollX * (isRtl ? -1 : 1);
      const y = travelled * scrollY;
      const scale = 1 + (zoom - 1) * progress;
      const angle = rotation * 360 * progress;
      element.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale}) rotate(${angle}deg)`;
    });
  }
}

uiHelpers.define('parallax-hero', ParallaxHero);

/* ---------- content-tabs ---------- */

class ContentTabs extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.buttons = Array.from(this.querySelectorAll('.content-tab-button'));
    this.tabs = Array.from(this.querySelectorAll('.content-tab'));
    if (!this.buttons.length) return;
    this.initialized = true;

    this.tabsContainer = this.querySelector('.content-tabs__tabs');
    this.activeBackground = this.querySelector('.content-tab-buttom__active-bg');
    this.buttonList = this.querySelector('.content-tabs__buttons');
    this.activeIndex = Math.max(
      this.buttons.findIndex((button) => button.classList.contains('content-tab-button--active')),
      0
    );

    this.buttonList?.setAttribute('role', 'tablist');
    this.buttons.forEach((button, index) => {
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', String(index === this.activeIndex));
      button.addEventListener('click', () => this.select(index));
      button.addEventListener('keydown', (event) => this.onKeyDown(event, index));
    });
    this.tabs.forEach((tab) => tab.setAttribute('role', 'tabpanel'));

    this.onBlockSelect = (event) => {
      if (!this.contains(event.target)) return;
      const index = Number.parseInt(event.target.dataset.index, 10);
      if (!Number.isNaN(index)) this.select(index);
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);

    if (this.activeBackground) {
      this.resizeObserver = new ResizeObserver(() => this.positionActiveBackground());
      this.resizeObserver.observe(this.buttonList || this);
      this.positionActiveBackground();
    }
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  onKeyDown(event, index) {
    const keys = { ArrowRight: 1, ArrowLeft: -1 };
    if (!(event.key in keys)) return;
    event.preventDefault();
    const step = keys[event.key] * (isRtl ? -1 : 1);
    const next = (index + step + this.buttons.length) % this.buttons.length;
    this.select(next);
    this.buttons[next].focus();
  }

  select(index) {
    if (index === this.activeIndex || !this.buttons[index]) return;

    const container = this.tabsContainer;
    const startHeight = container?.offsetHeight;

    this.buttons.forEach((button, i) => {
      const active = i === index;
      button.classList.toggle('content-tab-button--active', active);
      button.setAttribute('aria-selected', String(active));
    });
    this.tabs.forEach((tab, i) => tab.classList.toggle('content-tab--active', i === index));
    this.activeIndex = index;
    this.positionActiveBackground();

    // Animate the container height between tabs, then hand control back to CSS.
    const activeTab = this.tabs[index];
    if (!container || !activeTab || uiHelpers.reducedMotion.matches) return;
    container.style.height = `${startHeight}px`;
    requestAnimationFrame(() => {
      container.style.height = `${activeTab.offsetHeight}px`;
      const reset = () => container.style.removeProperty('height');
      container.addEventListener('transitionend', reset, { once: true });
      setTimeout(reset, 600);
    });
  }

  positionActiveBackground() {
    const background = this.activeBackground;
    const button = this.buttons[this.activeIndex];
    if (!background || !button) return;

    const parent = background.offsetParent || this.buttonList;
    if (!parent) return;
    const parentRect = parent.getBoundingClientRect();
    const rect = button.getBoundingClientRect();

    background.style.left = `${rect.left - parentRect.left - parent.clientLeft}px`;
    background.style.top = `${rect.top - parentRect.top - parent.clientTop}px`;
    background.style.width = `${rect.width}px`;
    background.style.height = `${rect.height}px`;
  }
}

uiHelpers.define('content-tabs', ContentTabs);

/* ---------- insta-stories ---------- */

class InstaStories extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.modal = this.querySelector('.insta-stories__modal');
    this.slider = this.querySelector('.insta-stories__slider');
    this.stories = Array.from(this.querySelectorAll('.insta-story'));
    if (!this.modal || !this.slider || !this.stories.length) return;
    this.initialized = true;

    this.activeIndex = 0;
    this.muted = true;
    this.userPaused = false;
    this.holdPaused = false;

    this.stories.forEach((story) => this.buildProgress(story));
    this.setupOpenButtonsScroller();

    this.onKeyDown = this.onKeyDown.bind(this);
    this.onResize = debounce(() => this.positionSlider(true), 100);

    this.addEventListener('click', this.onClick.bind(this));
    this.slider.addEventListener('animationend', this.onProgressEnd.bind(this));
    this.setupHoldToPause();

    this.onBlockSelect = (event) => {
      if (!this.contains(event.target)) return;
      const index = Number.parseInt(event.target.dataset.index, 10);
      if (!Number.isNaN(index)) this.open(index);
    };
    this.onBlockDeselect = (event) => {
      if (this.contains(event.target)) this.close();
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  disconnectedCallback() {
    if (!this.initialized) return;
    this.close();
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    this.openButtonsObserver?.disconnect();
  }

  get isOpen() {
    return this.modal.dataset.open === 'true';
  }

  get paused() {
    return this.userPaused || this.holdPaused;
  }

  get activeStory() {
    return this.stories[this.activeIndex];
  }

  mediaOf(story) {
    return Array.from(story.querySelectorAll('.insta-story__media'));
  }

  activeMediaIndex(story = this.activeStory) {
    return Number.parseInt(story.dataset.activeMediaIndex, 10) || 0;
  }

  buildProgress(story) {
    const progress = story.querySelector('.insta-story__progress');
    if (!progress) return;
    progress.innerHTML = '';
    this.mediaOf(story).forEach(() => {
      const item = document.createElement('span');
      item.className = 'insta-story__progress-item';
      const bar = document.createElement('span');
      bar.className = 'insta-story__progress-bar';
      item.appendChild(bar);
      progress.appendChild(item);
    });
  }

  /* Open/close */

  open(index = 0) {
    if (!this.isOpen) {
      this.opener = document.activeElement;
      this.modal.dataset.open = 'true';
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.onKeyDown);
      window.addEventListener('resize', this.onResize);
      this.setStory(index, 0, true);
      trapFocus(this.modal, this.activeStory.querySelector('.insta-story__pause-resume-btn'));
      return;
    }
    this.setStory(index, 0);
  }

  close() {
    if (!this.isOpen) return;
    uiHelpers.pauseMediaIn(this.modal);
    this.modal.dataset.open = 'false';
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('resize', this.onResize);
    this.holdPaused = false;
    this.slider.classList.remove('insta-stories__slider--paused');
    removeTrapFocus(this.opener && this.contains(this.opener) ? this.opener : null);
  }

  /* Navigation */

  setStory(index, mediaIndex = 0, instant = false) {
    const target = Math.min(Math.max(index, 0), this.stories.length - 1);
    if (target !== this.activeIndex) uiHelpers.pauseMediaIn(this.activeStory);

    this.activeIndex = target;
    this.stories.forEach((story, i) => story.classList.toggle('insta-story--active', i === target));
    this.positionSlider(instant);
    this.showMedia(this.activeStory, mediaIndex);
  }

  positionSlider(instant = false) {
    const width = this.stories[0].offsetWidth;
    const offset = this.activeIndex * width * (isRtl ? 1 : -1);

    if (instant) this.slider.style.transition = 'none';
    this.slider.style.transform = `translateX(${offset}px)`;
    if (instant) {
      this.slider.getBoundingClientRect(); // flush so the jump is not animated
      this.slider.style.removeProperty('transition');
    }
  }

  showMedia(story, mediaIndex) {
    const media = this.mediaOf(story);
    const items = Array.from(story.querySelectorAll('.insta-story__progress-item'));
    if (!media.length) return;

    const index = Math.min(Math.max(mediaIndex, 0), media.length - 1);
    story.dataset.activeMediaIndex = index;

    media.forEach((element, i) => {
      element.style.display = i === index ? 'block' : 'none';
      if (i !== index) element.querySelector('video')?.pause();
    });

    const current = media[index];
    const duration = this.durationOf(current);

    items.forEach((item, i) => {
      item.classList.toggle('insta-story__progress-item--completed', i < index);
      item.classList.remove('insta-story__progress-item--active');
    });
    const activeItem = items[index];
    if (activeItem) {
      activeItem.style.setProperty('--duration', `${duration}s`);
      void activeItem.offsetWidth; // restart the CSS animation
      activeItem.classList.add('insta-story__progress-item--active');
    }

    const timePosted = story.querySelector('.insta-story__time-posted');
    if (timePosted) timePosted.textContent = current.dataset.timePosted || '';

    const video = current.querySelector('video');
    if (video) {
      video.currentTime = 0;
      video.muted = this.muted;
      if (!duration || duration <= 0) {
        video.addEventListener(
          'loadedmetadata',
          () => activeItem?.style.setProperty('--duration', `${video.duration}s`),
          { once: true }
        );
      }
    }

    this.syncControls();
    this.applyPlaybackState();
  }

  durationOf(media) {
    const fallback = media.dataset.type === 'video' ? 15 : 10;
    const value = uiHelpers.toNumber(media.dataset.duration, 0);
    if (value > 0) return value;
    const video = media.querySelector('video');
    return video && Number.isFinite(video.duration) && video.duration > 0 ? video.duration : fallback;
  }

  next() {
    const story = this.activeStory;
    const mediaIndex = this.activeMediaIndex(story);

    if (mediaIndex < this.mediaOf(story).length - 1) {
      this.showMedia(story, mediaIndex + 1);
    } else if (this.activeIndex < this.stories.length - 1) {
      this.setStory(this.activeIndex + 1, 0);
    } else {
      this.close();
    }
  }

  prev() {
    const story = this.activeStory;
    const mediaIndex = this.activeMediaIndex(story);

    if (mediaIndex > 0) {
      this.showMedia(story, mediaIndex - 1);
    } else if (this.activeIndex > 0) {
      this.setStory(this.activeIndex - 1, 0);
    } else {
      this.showMedia(story, 0);
    }
  }

  onProgressEnd(event) {
    if (!this.isOpen || !event.target.classList.contains('insta-story__progress-bar')) return;
    const item = event.target.parentElement;
    if (!item.classList.contains('insta-story__progress-item--active')) return;
    if (!this.activeStory.contains(item)) return;
    this.next();
  }

  /* Playback */

  applyPlaybackState() {
    const story = this.activeStory;
    const state = this.paused ? 'paused' : 'running';
    story.querySelectorAll('.insta-story__progress-bar').forEach((bar) => {
      bar.style.animationPlayState = state;
    });

    const media = this.mediaOf(story)[this.activeMediaIndex(story)];
    const video = media?.querySelector('video');
    if (!video) return;
    if (this.paused || !this.isOpen) {
      video.pause();
    } else {
      video.play().catch(() => {});
    }
  }

  togglePause() {
    this.userPaused = !this.userPaused;
    this.syncControls();
    this.applyPlaybackState();
  }

  toggleMute() {
    this.muted = !this.muted;
    this.modal.querySelectorAll('.insta-story__media video').forEach((video) => {
      video.muted = this.muted;
    });
    this.syncControls();
  }

  syncControls() {
    this.modal.querySelectorAll('.insta-story__pause-resume-btn').forEach((button) => {
      button.dataset.paused = String(this.userPaused);
    });
    this.modal.querySelectorAll('.insta-story__volume-btn').forEach((button) => {
      button.dataset.muted = String(this.muted);
    });

    const atStart = this.activeIndex === 0 && this.activeMediaIndex() === 0;
    this.activeStory.querySelectorAll('.insta-story__arrow--prev').forEach((arrow) => {
      arrow.toggleAttribute('disabled', atStart);
    });
  }

  // Press and hold on the active story pauses it, like the native app.
  setupHoldToPause() {
    const release = () => {
      clearTimeout(this.holdTimer);
      if (!this.holdPaused) return;
      this.holdPaused = false;
      this.suppressNextClick = true;
      this.slider.classList.remove('insta-stories__slider--paused');
      this.applyPlaybackState();
    };

    this.slider.addEventListener('pointerdown', (event) => {
      if (!this.isOpen || event.button > 0) return;
      this.suppressNextClick = false;
      if (!event.target.closest('.insta-story--active')) return;
      if (event.target.closest('.insta-story__info__btn, .insta-story__arrow')) return;

      clearTimeout(this.holdTimer);
      this.holdTimer = setTimeout(() => {
        this.holdPaused = true;
        this.slider.classList.add('insta-stories__slider--paused');
        this.applyPlaybackState();
      }, 250);
    });
    ['pointerup', 'pointercancel', 'pointerleave'].forEach((type) => {
      this.slider.addEventListener(type, release);
    });
  }

  /* Events */

  onClick(event) {
    if (this.suppressNextClick) {
      this.suppressNextClick = false;
      if (this.slider.contains(event.target)) return;
    }

    const target = event.target;
    const container = this.querySelector('.insta-stories__slider-container');
    if (target === this.modal || target === container || target === this.slider) {
      this.close();
      return;
    }

    const button = target.closest('button');
    if (!button || !this.contains(button)) return;
    const index = Number.parseInt(button.dataset.index, 10);

    if (button.matches('.insta-story-open-btn')) this.open(index);
    else if (button.matches('.insta-stories__close-button')) this.close();
    else if (button.matches('.insta-story__slide-btn')) this.setStory(index, 0);
    else if (button.matches('.insta-story__prev')) this.prev();
    else if (button.matches('.insta-story__next')) this.next();
    else if (button.matches('.insta-story__pause-resume-btn')) this.togglePause();
    else if (button.matches('.insta-story__volume-btn')) this.toggleMute();
    else if (button.matches('.insta-stories__open-btns-prev')) this.scrollOpenButtons(-1);
    else if (button.matches('.insta-stories__open-btns-next')) this.scrollOpenButtons(1);
  }

  onKeyDown(event) {
    if (!this.isOpen) return;
    if (uiHelpers.isEscape(event)) {
      this.close();
    } else if (event.key === 'ArrowRight') {
      isRtl ? this.prev() : this.next();
    } else if (event.key === 'ArrowLeft') {
      isRtl ? this.next() : this.prev();
    }
  }

  /* Row of profile buttons */

  setupOpenButtonsScroller() {
    this.openButtonsContainer = this.querySelector('.insta-stories__open-buttons-container');
    this.openButtonsPrev = this.querySelector('.insta-stories__open-btns-prev');
    this.openButtonsNext = this.querySelector('.insta-stories__open-btns-next');
    const container = this.openButtonsContainer;
    if (!container) return;

    // Allow touch scrolling while keeping the scrollbar hidden.
    container.style.overflowX = 'auto';
    container.style.scrollbarWidth = 'none';
    container.addEventListener('scroll', () => this.updateOpenButtonArrows(), { passive: true });

    this.openButtonsObserver = new ResizeObserver(() => this.updateOpenButtonArrows());
    this.openButtonsObserver.observe(container);
    this.updateOpenButtonArrows();
  }

  updateOpenButtonArrows() {
    const container = this.openButtonsContainer;
    if (!container || !this.openButtonsPrev || !this.openButtonsNext) return;

    const maxScroll = container.scrollWidth - container.clientWidth;
    const overflowing = maxScroll > 1;
    const display = overflowing ? 'grid' : '';
    this.openButtonsPrev.style.display = display;
    this.openButtonsNext.style.display = display;
    if (!overflowing) return;

    const fromLeft = isRtl ? maxScroll + container.scrollLeft : container.scrollLeft;
    this.openButtonsPrev.toggleAttribute('disabled', fromLeft <= 1);
    this.openButtonsNext.toggleAttribute('disabled', fromLeft >= maxScroll - 1);
  }

  scrollOpenButtons(direction) {
    const container = this.openButtonsContainer;
    if (!container) return;
    container.scrollBy({ left: direction * container.clientWidth * 0.8, behavior: 'smooth' });
  }
}

uiHelpers.define('insta-stories', InstaStories);

/* ---------- contact-form ---------- */

class ContactForm extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.initialized = true;

    this.form.setAttribute('novalidate', '');
    this.form.addEventListener('submit', this.onSubmit.bind(this));
    this.form.addEventListener('input', (event) => {
      const wrapper = event.target.closest('.field-wrapper');
      if (wrapper?.classList.contains('field-wrapper--error')) this.validateField(wrapper);
    });
  }

  fieldOf(wrapper) {
    return wrapper.querySelector('input:not([type="hidden"]), textarea, select');
  }

  isRequired(wrapper, field) {
    return wrapper.dataset.required === 'true' || field.getAttribute('aria-required') === 'true';
  }

  validateField(wrapper) {
    const field = this.fieldOf(wrapper);
    if (!field) return true;

    const value = field.value.trim();
    let valid = true;
    if (this.isRequired(wrapper, field) && !value) valid = false;
    else if (value && field.type === 'email') valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    else if (value && field.pattern) valid = new RegExp(`^(?:${field.pattern})$`).test(value);

    const message = wrapper.querySelector('.contact__field-error');
    wrapper.classList.toggle('field-wrapper--error', !valid);
    message?.classList.toggle('hidden', valid);

    if (valid) {
      field.removeAttribute('aria-invalid');
    } else {
      field.setAttribute('aria-invalid', 'true');
      if (message) {
        if (!message.id) message.id = `${field.id || 'ContactField'}-error`;
        field.setAttribute('aria-describedby', message.id);
      }
    }
    return valid;
  }

  onSubmit(event) {
    const invalid = Array.from(this.form.querySelectorAll('.field-wrapper')).filter(
      (wrapper) => !this.validateField(wrapper)
    );
    if (!invalid.length) return;

    event.preventDefault();
    this.fieldOf(invalid[0])?.focus();
  }
}

uiHelpers.define('contact-form', ContactForm);
